import type { ThreadRuntime } from '@assistant-ui/react';
import type { ChatActionDirective, SummarizeLength, TranslateTarget } from '@iq/contracts/v1/chat';

/**
 * Slash-command actions the user can fire from the composer.
 *
 * `kind` discriminates which backend branch the request takes (see
 * `apps/api/src/v1/chat/directives.ts` `extractDirective`). Both branches
 * carry a parameter — summarize takes a `length`, translate takes a
 * `targetLanguage` — that the action prompt template consumes server-side.
 */
export type RunActionArgs =
  | { kind: 'summarize'; length: SummarizeLength }
  | { kind: 'translate'; targetLanguage: TranslateTarget };

/**
 * Serialize the action as text in the directive format
 * `unstable_defaultDirectiveFormatter` from `@assistant-ui/core` parses.
 *
 * The format is `:type[Label]{name=id}` — the same shape that
 * `DirectiveText` (`apps/web/src/components/ui/directive-text.tsx`) tokenizes
 * back into a Badge chip on render. Without this exact syntax the persisted
 * user message renders as plain text and the user sees the raw slash string
 * (`/summarize medium`) in their bubble instead of a clean "Summarize" pill.
 *
 * `type` doubles as the iconMap key in `thread.tsx`'s `createDirectiveText`
 * call — so `summarize` → FileText icon, `translate` → Languages icon, the
 * same icons the composer slash-palette uses.
 *
 * `name` (id) carries the parameter so the parsed segment retains the
 * sub-choice (`name=medium` for summarize, `name=en` for translate). The
 * Badge label stays static; the param shows up in the underlying
 * `data-directive-id` attribute for a11y / future affordances.
 */
function actionDirectiveText(args: RunActionArgs): string {
  if (args.kind === 'summarize') {
    return `:summarize[Summarize]{name=${args.length}}`;
  }
  return `:translate[Translate]{name=${args.targetLanguage}}`;
}

/**
 * Fire a chat-action directive (summarize / translate).
 *
 * Two layers travel with the message and the backend consumes them
 * independently:
 *   1. The visible text part — formatted as `:type[Label]{name=id}` so
 *      `DirectiveText` can re-tokenize it into a chip on render.
 *   2. `metadata.custom.directive` — the structured `ChatActionDirective`
 *      that the backend's `extractDirective` reads on the metadata-first
 *      path. This is what actually routes the request to the action branch
 *      and supplies the params (`length`, `targetLanguage`) to the action
 *      prompt template (`apps/api/src/v1/chat/prompts/action.ts`).
 *
 * After the append, we explicitly clear the composer via
 * `composer.setText('')`. The slash-popover (`unstable_useSlashCommandAdapter`
 * in `thread.tsx`) inserts a Lexical chip for the picked command when the
 * user selects it; without this clear the chip lingers in the composer
 * across turns. See the user's screenshot reproducing the "Summarize"
 * blue chip stuck after send.
 */
export function runChatAction(threadRuntime: ThreadRuntime, args: RunActionArgs): void {
  const directive: ChatActionDirective =
    args.kind === 'summarize'
      ? { action: 'summarize', length: args.length }
      : { action: 'translate', targetLanguage: args.targetLanguage };

  threadRuntime.append({
    role: 'user',
    content: [{ type: 'text', text: actionDirectiveText(args) }],
    metadata: { custom: { directive } },
  });
  threadRuntime.composer.setText('');
}
